import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import PageHeader from '../../components/common/PageHeader';
import { Link } from 'react-router-dom';
import { FaChartLine, FaExchangeAlt, FaLock, FaGlobe } from 'react-icons/fa';

const LiquidTokenPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const breadcrumbs = [
    { label: 'Fund', path: '/fund' },
    { label: 'Blockchain Fund', path: '/fund/blockchain' },
    { label: 'Liquid Token' }
  ];

  const features = [
    {
      icon: <FaChartLine className="w-8 h-8 text-secondary" />,
      title: "High Liquidity",
      description: "Our liquid token investments ensure you can enter or exit positions quickly without significant price impact."
    },
    {
      icon: <FaExchangeAlt className="w-8 h-8 text-secondary" />,
      title: "Diverse Trading Pairs",
      description: "Access tokens with multiple trading pairs across top exchanges, maximizing trading opportunities."
    },
    {
      icon: <FaLock className="w-8 h-8 text-secondary" />,
      title: "Risk Management",
      description: "Sophisticated risk assessment models and position sizing to mitigate downside exposure."
    },
    {
      icon: <FaGlobe className="w-8 h-8 text-secondary" />,
      title: "Global Market Access",
      description: "24/7 access to global crypto markets through our institutional-grade trading infrastructure."
    }
  ];

  return (
    <>
      <PageHeader 
        title="Liquid Token" 
        breadcrumbs={breadcrumbs}
      />
      
      <section className="py-8 md:py-24 bg-background">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-primary">
                Liquid Token Investment Strategy
              </h2>
              <p className="text-lg text-gray-700">
                Our Liquid Token portfolio provides investors with exposure to established cryptocurrencies and tokens with high market capitalization, trading volume, and liquidity profiles.
              </p>
              <p className="text-lg text-gray-700">
                Unlike illiquid investments, our liquid token strategy allows for dynamic position management, quick adaptation to market conditions, and efficient capital allocation across the crypto ecosystem.
              </p>
              <div className="pt-4">
                <Link 
                  to="/contact-us" 
                  className="px-8 py-3 bg-secondary hover:bg-secondary-dark text-white font-semibold rounded-lg transition-colors inline-block"
                >
                  Speak to an Investment Advisor
                </Link>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
            >
              <img 
                src="/assets/traders-discussing-digital-currency-focus-real-time-market-graph-sellable_31965-465579.jpg_swhfj0.jpg" 
                alt="Liquid Token Trading" 
                className="rounded-xl shadow-lg w-full h-auto object-cover"
              />
            </motion.div>
          </div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h3 className="text-2xl md:text-3xl font-heading font-bold text-primary mb-8">
              Key Benefits of Our Liquid Token Portfolio
            </h3>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl p-8 shadow-md hover:shadow-lg transition-all duration-300"
              >
                <div className="mb-4">{feature.icon}</div>
                <h4 className="text-xl font-heading font-semibold mb-3">{feature.title}</h4>
                <p className="text-gray-600">{feature.description}</p>
              </motion.div>
            ))}
          </div>
          
          <div className="mt-16 bg-gray-50 dark:bg-gray-800 rounded-xl p-8 shadow-inner">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="text-4xl font-bold text-secondary mb-2">35+</div>
                <div className="text-gray-600 dark:text-gray-300">Assets in Portfolio</div>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="text-4xl font-bold text-secondary mb-2">$50M+</div>
                <div className="text-gray-600 dark:text-gray-300">Assets Under Management</div>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="text-4xl font-bold text-secondary mb-2">24.5%</div>
                <div className="text-gray-600 dark:text-gray-300">Historical Annual Return</div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default LiquidTokenPage;
